import React from 'react';
import { Tooltip } from 'react-tooltip';
import cn from 'classnames';
// Helpers
import { formatCharacterCounter } from 'helpers/input';
// UI
import Text from 'ui3/Text/Text';
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
// Styles
import styles from './InputFooter.module.scss';
import 'react-tooltip/dist/react-tooltip.css';

type InputFooterProps = {
  hint?: string;
  notice?: string;
  error?: string | boolean; // TextField and Select had string or boolean, others have only string
  showCounter?: boolean;
  currentLength?: number;
  maxLength?: number;
  tooltipId: string;
  className?: string;
  errorClassName?: string;
};

export const InputFooter: React.FC<InputFooterProps> = ({
  hint,
  notice,
  error,
  showCounter,
  currentLength = 0,
  maxLength,
  tooltipId,
  className,
  errorClassName,
}) => {
  const hasError = Boolean(error);
  const shouldShowCounter = showCounter && maxLength;
  const shouldShowHintOrNotice = !hasError && (hint || notice);

  const shouldNotShowFooter =
    !hasError && !shouldShowCounter && !shouldShowHintOrNotice;

  if (shouldNotShowFooter) {
    return null;
  }

  return (
    <div className={cn(styles.footerWrapper, className)}>
      <div className={styles.footerLeft}>
        {hasError && (
          <Text
            variant="captionRegular"
            color="error-default"
            className={errorClassName}
          >
            {typeof error === 'string' ? error : ''}
          </Text>
        )}
        {shouldShowHintOrNotice && (
          <>
            {hint && (
              <>
                <MillionsIcon
                  id={tooltipId}
                  name="infoCircleOutline"
                  size={16}
                  className={styles.infoIcon}
                />
                <Tooltip
                  anchorId={tooltipId}
                  place="right"
                  className={styles.hint}
                >
                  {hint}
                </Tooltip>
              </>
            )}
            {notice && (
              <Text variant="captionRegular" color="lights-low">
                {notice}
              </Text>
            )}
          </>
        )}
      </div>
      {shouldShowCounter && maxLength && (
        <Text
          variant="captionRegular"
          color={hasError ? 'error-default' : 'lights-low'}
        >
          {formatCharacterCounter(currentLength, maxLength)}
        </Text>
      )}
    </div>
  );
};
